import React from 'react';
import { Box, Container, Typography, Button, Stack, Chip } from '@mui/material';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { ThaiStar, ThaiQuatrefoil, ThaiNestedMotif } from './ThaiMotif';

const Hero: React.FC = () => {
  return (
    <Box
      id="hero"
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        pt: { xs: 10, md: 0 },
        pb: { xs: 14, md: 0 },
        position: 'relative',
        overflow: 'hidden',
        background: 'linear-gradient(180deg, #FAF6EE 0%, #F5F0E8 100%)',
      }}
    >
      {/* ─── Thai Pra Jum Yam decorative motifs ─── */}
      <ThaiStar size={240} opacity={0.06} sx={{ top: '5%', right: '5%' }} />
      <ThaiNestedMotif
        size={120}
        opacity={0.08}
        innerColor="#FAF6EE"
        sx={{ top: '14%', right: '11%' }}
      />
      <ThaiQuatrefoil
        size={100}
        opacity={0.06}
        strokeOnly
        sx={{ bottom: '18%', left: '5%' }}
      />
      <ThaiQuatrefoil size={50} opacity={0.04} sx={{ top: '35%', left: '2%' }} />
      <ThaiStar size={50} opacity={0.04} rotate={15} sx={{ bottom: '12%', right: '18%' }} />
      <ThaiStar size={35} opacity={0.03} rotate={-10} sx={{ top: '8%', left: '20%' }} />

      {/* ─── Main Content ─── */}
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 4, md: 8 },
            flexDirection: { xs: 'column-reverse', md: 'row' },
          }}
        >
          {/* Left — Text content */}
          <Box className="animate-fade-in-up" sx={{ maxWidth: 620, flex: 1 }}>
            <Chip
              icon={
                <LocationOnIcon
                  sx={{ fontSize: '0.9rem !important', color: '#C8A24D !important' }}
                />
              }
              label="Bangkok, Thailand"
              variant="outlined"
              sx={{
                mb: 3,
                borderColor: 'rgba(0,0,0,0.1)',
                color: '#7A6E60',
                background: '#FFFFFF',
                fontWeight: 500,
              }}
            />

            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4.2rem' },
                lineHeight: 1.1,
                mb: 1,
                color: '#2A231C',
              }}
            >
              Hi, I'm{' '}
              <Box component="span" sx={{ color: '#C8A24D' }}>
                Bas
              </Box>
            </Typography>

            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '1.3rem', sm: '1.6rem', md: '1.9rem' },
                color: '#7A6E60',
                fontWeight: 400,
                mb: 3,
                fontFamily: '"Playfair Display", Georgia, serif',
                fontStyle: 'italic',
              }}
            >
              Software Engineer
            </Typography>

            <Typography
              variant="body1"
              sx={{
                fontSize: { xs: '1rem', md: '1.1rem' },
                color: '#7A6E60',
                maxWidth: 580,
                mb: 4,
                lineHeight: 1.8,
              }}
            >
              I build software that moves cities. As the lead engineer behind
              Bangkok's mass transit super app serving large-scale daily commuters, 
              I oversee the full product lifecycle from architecture to deployment. 
              My background spans cross-platform mobile apps, backend engineering,
              machine learning, and data science. I'm passionate about leading teams
              and building scalable, real-world solutions.
            </Typography>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Button
                variant="contained"
                size="large"
                href="#contact"
                startIcon={<EmailIcon />}
                sx={{
                  background: '#5C3D2E',
                  color: '#FFFFFF',
                  px: 4,
                  py: 1.5,
                  fontSize: '0.95rem',
                  boxShadow: '0 4px 14px rgba(92, 61, 46, 0.25)',
                  '&:hover': {
                    background: '#4A3124',
                    boxShadow: '0 6px 20px rgba(92, 61, 46, 0.35)',
                    transform: 'translateY(-1px)',
                  },
                  transition: 'all 0.25s ease',
                }}
                onClick={(e) => {
                  e.preventDefault();
                  document
                    .querySelector('#contact')
                    ?.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                Get In Touch
              </Button>
            </Stack>
          </Box>

          {/* Right — Profile photo */}
          <Box
            className="animate-fade-in-up"
            sx={{
              flexShrink: 0,
              position: 'relative',
              width: { xs: 240, sm: 280, md: 340 },
              height: { xs: 240, sm: 280, md: 340 },
            }}
          >
            {/* Decorative border ring */}
            <Box
              sx={{
                position: 'absolute',
                inset: -8,
                borderRadius: '50%',
                border: '2px solid rgba(200, 162, 77, 0.2)',
              }}
            />
            {/* Outer glow */}
            <Box
              sx={{
                position: 'absolute',
                inset: -16,
                borderRadius: '50%',
                border: '1px solid rgba(200, 162, 77, 0.08)',
              }}
            />
            {/* Photo */}
            <Box
              component="img"
              src="/profile.jpg"
              alt="Kasidej Cha-umpong (Bas)"
              sx={{
                width: '100%',
                height: '100%',
                borderRadius: '50%',
                objectFit: 'cover',
                objectPosition: '70% 15%',
                border: '4px solid #FFFFFF',
                boxShadow: '0 8px 32px rgba(92, 61, 46, 0.15), 0 2px 8px rgba(0,0,0,0.08)',
              }}
            />
            {/* Small Thai motif accent near photo */}
            <Box
              sx={{
                position: 'absolute',
                bottom: 8,
                right: 8,
              }}
            >
              <ThaiStar size={36} opacity={0.25} sx={{ position: 'relative' }} />
            </Box>
          </Box>
        </Box>
      </Container>

      {/* Scroll indicator */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 40,
          left: '50%',
          transform: 'translateX(-50%)',
          display: { xs: 'none', sm: 'flex' },
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1,
          cursor: 'pointer',
          opacity: 0.4,
          '&:hover': { opacity: 0.8 },
          transition: 'opacity 0.3s ease',
        }}
        onClick={() =>
          document
            .querySelector('#about')
            ?.scrollIntoView({ behavior: 'smooth' })
        }
      >
        <Typography
          variant="caption"
          sx={{ color: '#7A6E60', fontSize: '0.75rem', letterSpacing: 1 }}
        >
          Scroll Down
        </Typography>
        <ArrowDownwardIcon
          className="animate-float"
          sx={{ color: '#C8A24D', fontSize: '1rem' }}
        />
      </Box>
    </Box>
  );
};

export default Hero;
